import Hls from 'hls.js';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Collection, PreviewType } from '@/types/collection';
import { useCollection } from '@/providers/CollectionProvider';
import { Box, styled } from '@mui/material';
import { ReactComponent as HeartIcon } from '@/assets/images/heart.svg';
import { ReactComponent as ShareIcon } from '@/assets/images/share.svg';
import { ReactComponent as EllipsisIcon } from '@/assets/images/ellipsis.svg';
import { ReactComponent as PlayIcon } from '@/assets/images/play.svg';
import { ROUTES } from '@/constants/routes';
import CollectionRightSide from './CollectionRightSide';
import CollectionLeftSide from './CollectionLeftSide';
import analytics from '@/utils/analytics/analyticsInstance';

const Main = styled(Box)(() => ({
  flex: 1,
  position: 'relative',
  backgroundColor: 'black',
}));

const ViewContainer = styled(Box)(() => ({
  flex: 1,
  maxWidth: 480,
  width: '100%',
  height: '100%',
  display: 'flex',
  justifySelf: 'center',
  margin: 'auto',
  alignItems: 'center',
}));

const Controls = styled(Box)(() => ({
  inset: 0,
  padding: 16,
  gap: 16,
  position: 'absolute',
  display: 'grid',
  gridTemplateColumns: '1fr auto',
}));

const Image = styled('img')(() => ({
  maxWidth: '100%',
  width: '100%',
  height: '100dvh',
  objectFit: 'contain',
}));

const VideoContainer = styled(Box)(() => ({
  width: '100%',
  display: 'flex',
  position: 'relative',
  zIndex: 1,
}));

const Video = styled('video')(() => ({
  width: '100%',
  height: '100dvh',
}));

const Play = styled(PlayIcon)(() => ({
  width: 40,
  height: 40,
  opacity: 0.8,
  cursor: 'pointer',
  position: 'absolute',
  top: '50%',
  left: '50%',
  fill: 'white',
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.5)',
  transform: 'translate(-50%, -50%)',
}));

export default function CollectionView({
  products,
  expert,
  description,
  tags,
  totalLikes,
  totalShares,
  collectionPreview,
}: Collection) {
  const navigate = useNavigate();
  const { videoRef, isPause, handlePause } = useCollection();

  const handlePushToSignUpPage = async (element: string) => {
    await analytics.track('webContentElementClicked', {
      content_source: window.location.href,
      element,
    });

    navigate(ROUTES.SIGN_UP);
  };

  const renderView = () => {
    if (collectionPreview?.type === PreviewType.PHOTO) {
      return <Image src={collectionPreview.photoUrl} />;
    }

    if (collectionPreview?.type === PreviewType.VIDEO) {
      return (
        <VideoContainer>
          <Video ref={videoRef} onClick={handlePause} controls={false} playsInline loop />
          {isPause && <Play onClick={handlePause} />}
        </VideoContainer>
      );
    }

    return null;
  };

  useEffect(() => {
    if (!collectionPreview?.videoUrl || !videoRef?.current) return;

    if (Hls.isSupported()) {
      const hls = new Hls({ startLevel: -1 });

      hls.loadSource(collectionPreview.videoUrl);
      hls.attachMedia(videoRef.current);

      return () => hls.destroy();
    } else {
      videoRef.current.src = collectionPreview.videoUrl;
    }
  }, [videoRef, collectionPreview]);

  return (
    <Main>
      <ViewContainer>{renderView()}</ViewContainer>
      <Controls>
        <CollectionLeftSide
          onClick={handlePushToSignUpPage}
          expert={expert}
          tags={tags}
          productsCount={products.length}
          description={description}
        />
        <CollectionRightSide
          controls={[
            {
              id: 1,
              icon: <HeartIcon />,
              count: totalLikes,
              elementName: 'Like',
              onClick: handlePushToSignUpPage,
            },
            {
              id: 2,
              count: totalShares,
              icon: <ShareIcon />,
              elementName: 'Share',
              onClick: handlePushToSignUpPage,
            },
            {
              id: 3,
              icon: <EllipsisIcon />,
              elementName: 'Report',
              onClick: handlePushToSignUpPage,
            },
          ]}
        />
      </Controls>
    </Main>
  );
}
